import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react'

import type { FormikProps } from 'formik'

import {
  Stack,
  Box,
  Typography,
  Grid,
  GridItem,
  Skeleton,
  HStack,
  Form,
  Select,
  EmptyState,
} from '@pnld/components-web'

import {
  useDashboardCitiesByState,
  useDashboardComparativeEducationStages,
} from '@/api/dashboard/queries'
import { BarChart } from '@/components/Charts'
import SlidesDisplay from '@/components/Display/Slides'
import TotalizerDisplay from '@/components/Display/Totalizer'
import { stateOptions, stateToRegionMap } from '@/data/dashboard'
import { State } from '@/types/dashboard'

import { ComparativeSingleYearProps } from './types'

interface FilterFormValues {
  states: State[]
  cities: number[]
}

export const ComparativeSingleYear: React.FC<ComparativeSingleYearProps> = ({
  comparative,
  globalFilters,
}) => {
  const globalStates = globalFilters?.states || []
  const globalCities = globalFilters?.cities || []

  const initialValues: FilterFormValues = {
    states: globalStates.length > 0 ? globalStates : [],
    cities: globalCities.length > 0 ? globalCities : [],
  }

  const [formValues, setFormValues] = useState<FilterFormValues>(initialValues)
  const formRef = useRef<FormikProps<FilterFormValues> | null>(null)

  const getYearValues = useCallback(() => {
    const predictionYearStr = Array.isArray(globalFilters?.predictionYears)
      ? globalFilters.predictionYears[0]
      : globalFilters?.predictionYears || ''

    const censusYearStr = Array.isArray(globalFilters?.censusYears)
      ? globalFilters.censusYears[0]
      : globalFilters?.censusYears || ''

    const [predictionYear, baseCensusYear] = predictionYearStr.split('#')

    return {
      nuPredictionYear: Number(predictionYear),
      nuPredictionBaseCensusYear: Number(baseCensusYear),
      nuCensusYear: Number(censusYearStr),
    }
  }, [globalFilters?.predictionYears, globalFilters?.censusYears])

  const mapToBaseFilters = useCallback(
    (localStates: State[], localCities: number[]) => {
      return {
        coRegion: globalFilters?.region || undefined,
        coUf:
          localStates.length > 0
            ? localStates
            : globalFilters?.states?.length
            ? globalFilters.states
            : undefined,
        coCity:
          localCities.length > 0
            ? localCities
            : globalFilters?.cities?.length
            ? globalFilters.cities
            : undefined,
        coEntity: globalFilters?.schools?.length
          ? globalFilters.schools
          : undefined,
        tpDependency: globalFilters?.dependencies?.length
          ? globalFilters.dependencies
          : undefined,
        tpLocation: globalFilters?.locations?.length
          ? globalFilters.locations
          : undefined,
        coSphere: globalFilters?.spheres?.length
          ? globalFilters.spheres
          : undefined,
        stPnldService: globalFilters?.hasPndlService || undefined,
        coEducationStage: globalFilters?.educationStages?.length
          ? globalFilters.educationStages
          : undefined,
        coYearGrade: globalFilters?.yearGrades?.length
          ? globalFilters.yearGrades
          : undefined,
      }
    },
    [globalFilters]
  )

  const { data: cities, isLoading: isCitiesLoadingRaw } =
    useDashboardCitiesByState(
      formValues.states.length > 0 ? { coUf: formValues.states } : { coUf: [] },
      { enabled: formValues.states.length > 0 }
    )
  const isCitiesLoading = isCitiesLoadingRaw && formValues.states.length > 0

  const {
    data: comparativeEducationStages,
    isLoading: isComparativeEducationStagesLoading,
  } = useDashboardComparativeEducationStages(
    {
      ...getYearValues(),
      ...mapToBaseFilters(formValues.states, formValues.cities),
    },
    {
      enabled:
        !!comparative &&
        !!globalFilters?.predictionYears &&
        !!globalFilters?.censusYears,
    }
  )

  const handleOnSubmit = (values: FilterFormValues) => {
    setFormValues(values)
  }

  useEffect(() => {
    if (formRef.current) {
      const previousStates = formRef.current.values.states
      const currentStates = formValues.states

      const statesChanged =
        previousStates.length !== currentStates.length ||
        previousStates.some((state, index) => state !== currentStates[index])

      if (statesChanged) {
        formRef.current.setFieldValue('cities', [])
        setFormValues(prev => ({ ...prev, cities: [] }))
      }
    }
  }, [formValues.states])

  const availableStateOptions = useMemo(() => {
    if (globalStates.length > 0) {
      return stateOptions.filter(option => globalStates.includes(option.value))
    }

    if (globalFilters?.region) {
      return stateOptions.filter(
        option => stateToRegionMap[option.value] === globalFilters.region
      )
    }

    return stateOptions
  }, [globalStates, globalFilters?.region])

  const availableCityOptions = useMemo(() => {
    if (formValues.states.length > 0) {
      return (
        cities?.map(city => ({
          label: city.label,
          value: city.value,
        })) || []
      )
    }
    return []
  }, [formValues.states, cities])

  const isStateDisabled = globalStates.length > 0
  const isCityDisabled =
    globalCities.length > 0 || formValues.states.length === 0

  const transformChartData = (
    data: { label: string; prediction: number; census: number }[]
  ) =>
    data.map(item => ({
      name: item.label,
      Censo: item.census,
      Predição: item.prediction,
    }))

  return (
    <Stack gap={6}>
      <Grid templateColumns="repeat(3, 1fr)" gap={6}>
        <GridItem>
          <TotalizerDisplay
            number={comparative.census}
            label="Total de alunos Censo"
            icon="user-graduate"
            variant="primary"
            fullWidth
          />
        </GridItem>
        <GridItem>
          <TotalizerDisplay
            number={comparative.prediction}
            label="Total de alunos preditos"
            icon="user-graduate"
            variant="highlight"
            fullWidth
          />
        </GridItem>
        <GridItem>
          <TotalizerDisplay
            number={comparative.stdPercent}
            label="Desvio padrão"
            icon="chart-line"
            variant="highlight"
            fullWidth
          />
        </GridItem>
      </Grid>

      <Grid templateColumns="repeat(2, 1fr)" gap={3}>
        <GridItem>
          <TotalizerDisplay
            number={comparative.classes}
            label="Total de turmas informadas no Censo"
            icon="users"
            variant="primary"
            fullWidth
          />
        </GridItem>
        <GridItem>
          <TotalizerDisplay
            number={comparative.schools}
            label="Total de escolas informadas no Censo"
            icon="school"
            variant="primary"
            fullWidth
          />
        </GridItem>
      </Grid>

      {(['region', 'dependency', 'location', 'educationStage'] as const).map(
        key => (
          <Stack
            key={key}
            padding={4}
            gap={4}
            borderRadius="md"
            border="2px solid"
            borderColor="gray.200"
          >
            <Typography variant="h-xs" color="brand.primary.dark_2">
              {key === 'region'
                ? 'Regiões'
                : key === 'dependency'
                ? 'Dependências'
                : key === 'location'
                ? 'Localizações'
                : 'Etapas de Ensino'}
            </Typography>
            {transformChartData(comparative[key]).length > 0 ? (
              <Box height={300}>
                <BarChart data={transformChartData(comparative[key])} />
              </Box>
            ) : (
              <EmptyState
                title="Nenhum dado encontrado"
                description={`Não há dados de ${
                  key === 'region'
                    ? 'regiões'
                    : key === 'dependency'
                    ? 'dependências'
                    : key === 'location'
                    ? 'localizações'
                    : 'etapas de ensino'
                } para exibir.`}
                iconProps={{ name: 'chart-bar' }}
                shaped="false"
              />
            )}
          </Stack>
        )
      )}

      <Stack
        padding={4}
        gap={4}
        borderRadius="md"
        border="2px solid"
        borderColor="gray.200"
      >
        <Form
          innerRef={formRef}
          initialValues={initialValues}
          onSubmit={handleOnSubmit}
          submitWhenChange={true}
          enableReinitialize={true}
        >
          <HStack>
            <Select
              name="states"
              label="Filtrar por estado"
              placeholder="Todos"
              options={availableStateOptions}
              disabled={isStateDisabled}
              isMulti
            />
            <Select
              name="cities"
              label="Filtrar por Município"
              placeholder="Todos"
              options={availableCityOptions}
              disabled={isCityDisabled || isCitiesLoading}
              isMulti
              {...(isCitiesLoading && { isLoading: true })}
            />
          </HStack>
        </Form>

        {isComparativeEducationStagesLoading ? (
          <Skeleton height={360} borderRadius="md" />
        ) : comparativeEducationStages &&
          comparativeEducationStages.length > 0 &&
          comparativeEducationStages[0].yearGrades.length > 0 ? (
          <SlidesDisplay
            items={comparativeEducationStages.map(stage => ({
              title: stage.dsEducationStage,
              content: (
                <Box height={300}>
                  <BarChart
                    data={stage.yearGrades.map(grade => ({
                      name: grade.label,
                      Censo: grade.census,
                      Predição: grade.prediction,
                    }))}
                  />
                </Box>
              ),
            }))}
          />
        ) : (
          <EmptyState
            title="Nenhum dado encontrado"
            description="Não há dados de etapas de ensino para os filtros selecionados."
            iconProps={{ name: 'table' }}
            shaped="false"
          />
        )}
      </Stack>
    </Stack>
  )
}
